import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, Chip, Grid,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, Tab, Tabs, TextField, IconButton, Badge
} from '@mui/material';
import {
  AddBox, LocalShipping, CheckCircle, HourglassEmpty, Cancel, Refresh, AttachMoney, Campaign,
  Chat, Send, Close, AccountBalance, ContentCopy, MyLocation, FileDownload, Print
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import CustomerLayout from '../components/CustomerLayout';
import { customerOrdersAPI, customerMessagesAPI } from '../services/api';
import DriverLocationDialog from '../components/DriverLocationDialog';
import { fetchCompanyInfo } from '../services/companyInfo';

// 금액상태 (고객 표시용)
// payment_pending 은 고객 수락 여부에 따라 승인대기(미수락) → 결재대기(수락) 로 분기
const PRICE_STATUS_MAP = {
  pending_price:     { label: '금액검토',    color: 'default' },
  payment_pending:   { label: '결재대기',    color: 'warning' },
  deferred_payment:  { label: '추후결재',    color: 'secondary' },
  payment_completed: { label: '결재완료',    color: 'success' },
  refund_pending:    { label: '환불대기',    color: 'error' },
  refund_completed:  { label: '환불완료',    color: 'default' },
  no_payment:        { label: '결재금액없음', color: 'default' },
};

const priceStatusFor = (order) => {
  if (order.price_status === 'payment_pending') {
    return order.customer_accepted_at
      ? { label: '결재대기', color: 'warning' }
      : { label: '승인대기', color: 'info' };
  }
  return PRICE_STATUS_MAP[order.price_status] || { label: order.price_status || '-', color: 'default' };
};

// 배차상태 (고객 표시용 - 간소화)
const DISPATCH_STATUS_MAP = {
  before_dispatch: { label: '배차전',   color: 'default' },
  dispatch_ready:  { label: '배차대기', color: 'info' },
  broadcasting:    { label: '배차대기', color: 'info' },
  assigned:        { label: '배차완료', color: 'primary' },
  in_progress:     { label: '운송중',   color: 'primary' },
  completed:       { label: '완료',     color: 'success' },
  cancelled:       { label: '주문취소', color: 'error' },
};

// 구분 라벨
const DISPATCH_TYPE_LABEL = { urgent: '긴급', round_trip: '왕복', waypoint: '경유' };
const DISPATCH_TYPE_COLOR = { urgent: 'error', round_trip: 'info', waypoint: 'secondary' };

// JSON/배열 안전 파싱
function parseJsonField(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
}
function formatDt(s) {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// 탭 필터 (배차상태 기준)
const STATUS_TABS = ['all', 'before_dispatch', 'dispatch_ready', 'assigned', 'in_progress', 'completed', 'cancelled'];
const STATUS_LABELS = {
  all: '전체',
  before_dispatch: '배차전',
  dispatch_ready: '배차대기',
  assigned: '배차완료',
  in_progress: '운송중',
  completed: '완료',
  cancelled: '주문취소',
};

const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function MyOrdersPage({ onLogout }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [accountInfo, setAccountInfo] = useState(null);

  // 위치 추적
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [trackingOpen, setTrackingOpen] = useState(false);

  // 메시지
  const [messageDialog, setMessageDialog] = useState(false);
  const [messageOrder, setMessageOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [messageLoading, setMessageLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const messageOrderRef = useRef(null);
  const messageDialogRef = useRef(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [companyName, setCompanyName] = useState('');
  const companyNameRef = useRef('');
  useEffect(() => {
    fetchCompanyInfo().then((c) => {
      setCompanyName(c.company_name || '');
      companyNameRef.current = c.company_name || '';
    }).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    customerOrdersAPI.getAll()
      .then((res) => { setOrders(res.data.data || []); setError(''); })
      .catch(() => setError('주문 목록을 불러오는데 실패했습니다.'))
      .finally(() => setLoading(false));
  };

  const loadUnreadCounts = () => {
    customerMessagesAPI.getUnreadAll().then(res => setUnreadCounts(res.data.data || {})).catch(() => {});
  };

  useEffect(() => {
    load();
    loadUnreadCounts();
    customerOrdersAPI.getAccountInfo().then((res) => setAccountInfo(res.data.data)).catch(() => {});

    // Socket.IO 연결
    const socket = io(API_URL || window.location.origin);

    socket.on('order-message', (data) => {
      if (data.senderType === 'staff') {
        // 브라우저 알림
        if (Notification.permission === 'granted') {
          const cn = companyNameRef.current;
          new Notification(`${cn ? cn + ' - ' : ''}새 메시지`, {
            body: `[${data.orderNumber}] ${data.message}`,
          });
        }
        load();
        loadUnreadCounts();
        // 메시지 다이얼로그가 열려있고 같은 주문이면 새로고침
        if (messageDialogRef.current && messageOrderRef.current?.order_id === data.orderId) {
          customerMessagesAPI.getMessages(data.orderId).then(res => {
            setMessages(res.data.data || []);
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
          }).catch(() => {});
        }
      }
    });

    socket.on('order-updated', (data) => {
      load();
      loadUnreadCounts();
      if (data?.type === 'price_proposed' && Notification.permission === 'granted') {
        const cn2 = companyNameRef.current;
        new Notification(`${cn2 ? cn2 + ' - ' : ''}금액 안내`, { body: '배차 금액이 제시되었습니다. 확인해주세요.' });
      }
    });

    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => socket.disconnect();
  }, []);

  const closeMessageDialog = () => {
    setMessageDialog(false);
    messageDialogRef.current = false;
    messageOrderRef.current = null;
  };

  const openMessageDialog = async (order) => {
    setMessageOrder(order);
    messageOrderRef.current = order;
    setMessageDialog(true);
    messageDialogRef.current = true;
    setNewMessage('');
    try {
      const res = await customerMessagesAPI.getMessages(order.order_id);
      setMessages(res.data.data || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      loadUnreadCounts();
    } catch (err) {
      setMessages([]);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !messageOrder) return;
    setMessageLoading(true);
    try {
      await customerMessagesAPI.sendMessage(messageOrder.order_id, newMessage.trim());
      setNewMessage('');
      const res = await customerMessagesAPI.getMessages(messageOrder.order_id);
      setMessages(res.data.data || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      alert('메시지 전송 실패');
    }
    setMessageLoading(false);
  };

  const filteredOrders = tabValue === 0
    ? orders
    : orders.filter(o => {
        const tab = STATUS_TABS[tabValue];
        // dispatch_ready 탭은 broadcasting도 포함 (고객에게는 둘 다 배차대기)
        if (tab === 'dispatch_ready') return ['dispatch_ready', 'broadcasting'].includes(o.status);
        return o.status === tab;
      });

  const openDetail = (order) => { setSelectedOrder(order); setDetailOpen(true); };

  // CSV 다운로드 (UTF-8 BOM 포함 → 엑셀 호환)
  const downloadCsv = () => {
    const headers = [
      '주문번호','생성일','구분','출발지','도착지',
      '상차일시','상차방법','하차일시','하차방법',
      '차종','톤수','제시가','확정가','배차상태','결재상태','운전자',
    ];
    const rows = filteredOrders.map((o) => {
      const dispatchTypes = parseJsonField(o.dispatch_types).map((t) => DISPATCH_TYPE_LABEL[t] || t).join(', ');
      const loadingM = parseJsonField(o.loading_methods).join(', ');
      const unloadingM = parseJsonField(o.unloading_methods).join(', ');
      const vTypes = parseJsonField(o.vehicle_types);
      const vWeights = parseJsonField(o.vehicle_weights);
      return [
        o.order_number,
        o.created_at ? new Date(o.created_at).toLocaleString('ko-KR') : '',
        dispatchTypes,
        o.origin_address || '',
        o.destination_address || '',
        o.pickup_time ? new Date(o.pickup_time).toLocaleString('ko-KR') : '',
        loadingM,
        o.desired_delivery_datetime ? new Date(o.desired_delivery_datetime).toLocaleString('ko-KR') : '',
        unloadingM,
        (vTypes.length ? vTypes : [o.required_vehicle_type]).filter(Boolean).join(', '),
        (vWeights.length ? vWeights : (o.required_weight ? [o.required_weight + 't'] : [])).join(', '),
        o.client_offered_price ? Number(o.client_offered_price).toLocaleString() : '',
        o.confirmed_price ? Number(o.confirmed_price).toLocaleString() : '',
        DISPATCH_STATUS_MAP[o.status]?.label || o.status || '',
        PRICE_STATUS_MAP[o.price_status]?.label || o.price_status || '',
        o.driver_name ? `${o.driver_name} (${o.driver_phone || ''}) ${o.vehicle_number || ''}`.trim() : '',
      ];
    });
    const csv = [headers, ...rows]
      .map((r) => r.map((cell) => {
        const s = String(cell ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))
      .join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${companyName || '주문'}_주문목록_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  // 인쇄 (새 창에서 회사명 헤더 + 테이블 + 자동 print)
  const printOrders = () => {
    const today = new Date().toLocaleDateString('ko-KR');
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
    const rowsHtml = filteredOrders.map((o) => {
      const dispatchTypes = parseJsonField(o.dispatch_types).map((t) => DISPATCH_TYPE_LABEL[t] || t).join(', ');
      const vTypes = parseJsonField(o.vehicle_types);
      const vWeights = parseJsonField(o.vehicle_weights);
      return `<tr>
        <td>${esc(o.order_number)}</td>
        <td>${esc(o.created_at ? new Date(o.created_at).toLocaleDateString('ko-KR') : '')}</td>
        <td>${esc(dispatchTypes)}</td>
        <td>${esc(o.origin_address || '')}</td>
        <td>${esc(o.destination_address || '')}</td>
        <td>${esc(o.pickup_time ? new Date(o.pickup_time).toLocaleString('ko-KR') : '')}</td>
        <td>${esc(o.desired_delivery_datetime ? new Date(o.desired_delivery_datetime).toLocaleString('ko-KR') : '')}</td>
        <td>${esc((vTypes.length ? vTypes : [o.required_vehicle_type]).filter(Boolean).join(', '))}</td>
        <td>${esc((vWeights.length ? vWeights : (o.required_weight ? [o.required_weight + 't'] : [])).join(', '))}</td>
        <td style="text-align:right">${o.confirmed_price ? Number(o.confirmed_price).toLocaleString() : (o.client_offered_price ? Number(o.client_offered_price).toLocaleString() : '')}</td>
        <td>${esc(DISPATCH_STATUS_MAP[o.status]?.label || o.status || '')}</td>
        <td>${esc(o.driver_name || '')}</td>
      </tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(companyName || '주문 목록')}</title>
      <style>
        @page { size: A4 landscape; margin: 12mm; }
        body { font-family: 'Noto Sans KR', sans-serif; color: #111; }
        h1 { font-size: 18px; margin: 0 0 4px; }
        .meta { font-size: 11px; color: #666; margin-bottom: 12px; }
        table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
        th, td { border: 1px solid #bbb; padding: 4px 6px; text-align: left; vertical-align: top; }
        thead { background: #f0f0f0; }
        tr:nth-child(even) td { background: #fafafa; }
      </style></head><body>
      <h1>${esc(companyName || '')} 주문 목록</h1>
      <div class="meta">출력일: ${today} · 총 ${filteredOrders.length}건</div>
      <table>
        <thead><tr>
          <th>주문번호</th><th>생성일</th><th>구분</th><th>출발</th><th>도착</th>
          <th>상차일시</th><th>하차일시</th><th>차종</th><th>톤수</th>
          <th>금액</th><th>상태</th><th>운전자</th>
        </tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <script>window.onload = () => { setTimeout(() => { window.print(); }, 200); };</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) return alert('팝업이 차단되어 인쇄할 수 없습니다. 팝업 허용 후 다시 시도하세요.');
    w.document.open(); w.document.write(html); w.document.close();
  };

  const priceStatusInfo = (order) => priceStatusFor(order);
  const dispatchStatusInfo = (s) => DISPATCH_STATUS_MAP[s] || { label: s || '-', color: 'default' };

  return (
    <CustomerLayout onLogout={onLogout}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>내 주문 목록</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<FileDownload />} onClick={downloadCsv} variant="outlined" disabled={filteredOrders.length === 0}>엑셀</Button>
          <Button startIcon={<Print />} onClick={printOrders} variant="outlined" disabled={filteredOrders.length === 0}>인쇄</Button>
          <Button startIcon={<Refresh />} onClick={load} variant="outlined">새로고침</Button>
          <Button startIcon={<AddBox />} variant="contained" onClick={() => navigate('/orders/new')}>새 주문</Button>
        </Box>
      </Box>

      <Tabs
        value={tabValue} onChange={(_, v) => setTabValue(v)}
        variant="scrollable" scrollButtons="auto"
        sx={{ mb: 2, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}
      >
        {STATUS_TABS.map((s) => (
          <Tab key={s} label={`${STATUS_LABELS[s]} (${s === 'all' ? orders.length : s === 'dispatch_ready' ? orders.filter(o => ['dispatch_ready', 'broadcasting'].includes(o.status)).length : orders.filter(o => o.status === s).length})`} />
        ))}
      </Tabs>

      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && filteredOrders.length === 0 && (
        <Card sx={{ borderRadius: 2, textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary" gutterBottom>해당하는 주문이 없습니다.</Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/orders/new')}>
            배차 주문하기
          </Button>
        </Card>
      )}

      {filteredOrders.map((order) => {
        const ps = priceStatusInfo(order);
        const ds = dispatchStatusInfo(order.status);
        return (
          <Card
            key={order.order_id}
            sx={{ mb: 2, borderRadius: 2, cursor: 'pointer', '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}
            onClick={() => openDetail(order)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" fontWeight={700} color="primary">
                    {order.order_number}
                  </Typography>
                  <IconButton size="small" sx={{ p: 0.3 }}
                    onClick={(e) => { e.stopPropagation(); openMessageDialog(order); }}>
                    <Badge badgeContent={unreadCounts[order.order_id] || 0} color="error" max={99}>
                      <Chat fontSize="small" sx={{ color: unreadCounts[order.order_id] > 0 ? '#f44336' : '#9e9e9e', fontSize: 18 }} />
                    </Badge>
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip label={ps.label} color={ps.color} size="small" />
                  <Chip label={ds.label} color={ds.color} size="small" />
                  {order.photos?.length > 0 && (
                    <Chip label={`사진 ${order.photos.length}`} size="small" variant="outlined" />
                  )}
                </Box>
              </Box>

              <Grid container spacing={1}>
                <Grid item xs={12} sm={5}>
                  <Typography variant="caption" color="text.secondary">출발지</Typography>
                  <Typography variant="body2" noWrap>{order.origin_address}</Typography>
                </Grid>
                <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
                  <LocalShipping color="action" fontSize="small" sx={{ mt: { sm: 2 } }} />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Typography variant="caption" color="text.secondary">도착지</Typography>
                  <Typography variant="body2" noWrap>{order.destination_address}</Typography>
                </Grid>
              </Grid>

              {/* 신규 정보: 구분/일시/방법/차종/톤수 + 경유지 */}
              {(() => {
                const dTypes = parseJsonField(order.dispatch_types);
                const lMethods = parseJsonField(order.loading_methods);
                const uMethods = parseJsonField(order.unloading_methods);
                const vTypes = parseJsonField(order.vehicle_types);
                const vWeights = parseJsonField(order.vehicle_weights);
                const waypoints = parseJsonField(order.waypoints);
                const finalVTypes = vTypes.length ? vTypes : (order.required_vehicle_type ? [order.required_vehicle_type] : []);
                const finalVWeights = vWeights.length ? vWeights : (order.required_weight ? [order.required_weight + 't'] : []);
                const hasAny = dTypes.length || order.pickup_time || order.desired_delivery_datetime
                  || lMethods.length || uMethods.length || finalVTypes.length || finalVWeights.length || waypoints.length;
                if (!hasAny) return null;
                return (
                  <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #e0e0e0' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                      {dTypes.map((t) => (
                        <Chip key={`d-${t}`} label={DISPATCH_TYPE_LABEL[t] || t} size="small" color={DISPATCH_TYPE_COLOR[t] || 'default'} />
                      ))}
                      {order.pickup_time && (
                        <Chip label={`상차 ${formatDt(order.pickup_time)}`} size="small" variant="outlined" />
                      )}
                      {lMethods.length > 0 && (
                        <Chip label={`상차: ${lMethods.join(', ')}`} size="small" variant="outlined" color="primary" />
                      )}
                      {order.desired_delivery_datetime && (
                        <Chip label={`하차 ${formatDt(order.desired_delivery_datetime)}`} size="small" variant="outlined" />
                      )}
                      {uMethods.length > 0 && (
                        <Chip label={`하차: ${uMethods.join(', ')}`} size="small" variant="outlined" color="primary" />
                      )}
                      {finalVTypes.length > 0 && (
                        <Chip label={`차종: ${finalVTypes.join('/')}`} size="small" color="primary" />
                      )}
                      {finalVWeights.length > 0 && (
                        <Chip label={`톤수: ${finalVWeights.join('/')}`} size="small" color="secondary" />
                      )}
                    </Box>
                    {waypoints.length > 0 && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: '#f3e5f5', borderLeft: '3px solid #9c27b0', borderRadius: 0.5 }}>
                        <Typography variant="caption" color="secondary" fontWeight={700}>🚏 경유지 ({waypoints.length})</Typography>
                        {waypoints.map((w, i) => {
                          const addr = typeof w === 'string' ? w : w.address;
                          const cn = typeof w === 'object' ? w.contact_name : '';
                          const c = typeof w === 'object' ? w.contact : '';
                          return (
                            <Typography key={i} variant="caption" sx={{ display: 'block', mt: 0.3 }}>
                              {i + 1}. {addr}
                              {(cn || c) && <span style={{ color: '#666' }}> · {cn}{cn && c ? ' ' : ''}{c}</span>}
                            </Typography>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })()}

              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {new Date(order.created_at).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </Typography>
                {order.client_offered_price && (
                  <Typography variant="body2" fontWeight={700} color="warning.dark">
                    제시금액: {parseInt(order.client_offered_price).toLocaleString()}원
                  </Typography>
                )}
              </Box>

              {order.driver_name && (
                <Box sx={{ mt: 1, p: 1, bgcolor: '#e3f2fd', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="caption" color="primary">
                    배차 기사: {order.driver_name} ({order.driver_phone}) · {order.vehicle_number}
                  </Typography>
                  {['in_progress', 'assigned'].includes(order.status) && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<MyLocation fontSize="small" />}
                      onClick={(e) => { e.stopPropagation(); setTrackingOrder(order); setTrackingOpen(true); }}
                      sx={{ py: 0.3, fontSize: 12 }}
                    >
                      위치 보기
                    </Button>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* 입금 안내 (수락한 주문 또는 추후결재 주문이 있을 때만 표시) */}
      {!loading && accountInfo && (() => {
        const payable = orders.filter((o) =>
          (o.price_status === 'payment_pending' && o.customer_accepted_at) ||
          o.price_status === 'deferred_payment'
        );
        if (payable.length === 0) return null;
        const totalAmount = payable.reduce((sum, o) => sum + (parseInt(o.client_offered_price) || 0), 0);
        const copy = (text) => {
          navigator.clipboard?.writeText(text).then(
            () => { /* 성공 — 별도 표시 없이 진행 */ },
            () => { /* 실패 무시 */ }
          );
        };
        return (
          <Card sx={{ mt: 3, mb: 2, borderRadius: 2, border: '2px solid #f9a825', bgcolor: '#fffbf0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccountBalance sx={{ color: '#f9a825', fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700} color="warning.dark">
                  입금 안내
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                아래 계좌로 입금해 주시면 입금 확인 후 배차가 진행됩니다.
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #f0e0a0' }}>
                    <Typography variant="caption" color="text.secondary">은행</Typography>
                    <Typography variant="body1" fontWeight={700}>{accountInfo.bank_name || '-'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #f0e0a0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">계좌번호</Typography>
                      <Typography variant="body1" fontWeight={700}>{accountInfo.account_number || '-'}</Typography>
                    </Box>
                    {accountInfo.account_number && (
                      <IconButton size="small" onClick={() => copy(accountInfo.account_number)} title="계좌번호 복사">
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #f0e0a0' }}>
                    <Typography variant="caption" color="text.secondary">예금주</Typography>
                    <Typography variant="body1" fontWeight={700}>{accountInfo.account_holder || '-'}</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">입금 대상 주문</Typography>
                  <Typography variant="body2">
                    {payable.map(o => o.order_number).join(', ')}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">총 입금 금액</Typography>
                  <Typography variant="h5" fontWeight={700} color="error">
                    {totalAmount.toLocaleString()}원
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })()}

      {/* 상세 다이얼로그 */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        {selectedOrder && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography fontWeight={700}>{selectedOrder.order_number}</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Chip label={priceStatusInfo(selectedOrder).label} color={priceStatusInfo(selectedOrder).color} size="small" />
                  <Chip label={dispatchStatusInfo(selectedOrder.status).label} color={dispatchStatusInfo(selectedOrder.status).color} size="small" />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">출발지</Typography>
                  <Typography>{selectedOrder.origin_address}</Typography>
                  {selectedOrder.origin_contact && <Typography variant="body2" color="text.secondary">{selectedOrder.origin_contact}</Typography>}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">도착지</Typography>
                  <Typography>{selectedOrder.destination_address}</Typography>
                  {selectedOrder.destination_contact && <Typography variant="body2" color="text.secondary">{selectedOrder.destination_contact}</Typography>}
                </Grid>
                {selectedOrder.required_vehicle_type && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">차량 종류</Typography>
                    <Typography>{selectedOrder.required_vehicle_type}</Typography>
                  </Grid>
                )}
                {selectedOrder.client_offered_price && (
                  <Grid item xs={selectedOrder.payment_date ? 6 : 6}>
                    <Typography variant="caption" color="text.secondary">제시 금액 (입금할 금액)</Typography>
                    <Typography fontWeight={700} color="warning.dark">{parseInt(selectedOrder.client_offered_price).toLocaleString()}원</Typography>
                  </Grid>
                )}
                {selectedOrder.payment_date && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">송금 완료</Typography>
                    <Typography fontWeight={700} color="success.main">
                      {new Date(selectedOrder.payment_date).toLocaleString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Grid>
                )}
                {selectedOrder.special_notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">특이사항</Typography>
                    <Typography variant="body2">{selectedOrder.special_notes}</Typography>
                  </Grid>
                )}
                {selectedOrder.driver_name && (
                  <Grid item xs={12}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="caption" color="text.secondary">배차 기사</Typography>
                    <Typography>{selectedOrder.driver_name} · {selectedOrder.driver_phone}</Typography>
                    <Typography variant="body2" color="text.secondary">{selectedOrder.vehicle_number}</Typography>
                  </Grid>
                )}

                {/* 사진 */}
                {selectedOrder.photos?.length > 0 && (
                  <Grid item xs={12}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="caption" color="text.secondary">첨부 사진</Typography>
                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      {selectedOrder.photos.map((p) => (
                        <Grid item xs={6} key={p.photo_id}>
                          <img
                            src={p.file_path}
                            alt={p.original_name}
                            style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}

                {/* 금액 제시 응답 (결재대기 + 아직 수락 전) */}
                {selectedOrder.price_status === 'payment_pending' && !selectedOrder.customer_accepted_at && (
                  <Grid item xs={12}>
                    <Divider sx={{ mb: 1 }} />
                    <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #1976d2' }}>
                      <Typography variant="body2" fontWeight={700} color="primary" gutterBottom>
                        제시금액 응답
                      </Typography>
                      {selectedOrder.client_offered_price && (
                        <Typography variant="body1" fontWeight={700} sx={{ mb: 1 }}>
                          제시 금액: {parseInt(selectedOrder.client_offered_price).toLocaleString()}원
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained" color="success" size="small"
                          onClick={async () => {
                            try {
                              await customerOrdersAPI.respondPrice(selectedOrder.order_id, 'accept');
                              alert('금액을 수락했습니다. 입금 안내를 확인하세요.');
                              setDetailOpen(false);
                              load();
                            } catch (e) { alert(e.response?.data?.message || '처리 실패'); }
                          }}
                        >수락 (입금 진행)</Button>
                        <Button
                          variant="outlined" color="warning" size="small"
                          onClick={async () => {
                            if (!window.confirm('금액 재제시를 요청하시겠습니까?')) return;
                            try {
                              await customerOrdersAPI.respondPrice(selectedOrder.order_id, 'hold');
                              alert('보류되었습니다. 직원이 새 금액을 제시할 예정입니다.');
                              setDetailOpen(false);
                              load();
                            } catch (e) { alert(e.response?.data?.message || '처리 실패'); }
                          }}
                        >보류 (재제시 요청)</Button>
                        <Button
                          variant="outlined" color="error" size="small"
                          onClick={async () => {
                            if (!window.confirm('정말로 주문을 취소하시겠습니까?')) return;
                            try {
                              await customerOrdersAPI.respondPrice(selectedOrder.order_id, 'cancel');
                              alert('주문이 취소되었습니다.');
                              setDetailOpen(false);
                              load();
                            } catch (e) { alert(e.response?.data?.message || '처리 실패'); }
                          }}
                        >주문취소</Button>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* 입금 안내 (수락 후) */}
                {selectedOrder.price_status === 'payment_pending' && selectedOrder.customer_accepted_at && accountInfo && (
                  <Grid item xs={12}>
                    <Divider sx={{ mb: 1 }} />
                    <Box sx={{ p: 2, bgcolor: '#fff8e1', borderRadius: 1, border: '1px solid #f9a825' }}>
                      <Typography variant="body2" fontWeight={700} color="warning.dark" gutterBottom>
                        입금 안내
                      </Typography>
                      {selectedOrder.client_offered_price && (
                        <Typography variant="body1" fontWeight={700} color="error" sx={{ mb: 1 }}>
                          입금 금액: {parseInt(selectedOrder.client_offered_price).toLocaleString()}원
                        </Typography>
                      )}
                      <Typography variant="body2">
                        {accountInfo.bank_name} · {accountInfo.account_number} · {accountInfo.account_holder}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        입금 확인 후 배차가 진행됩니다.
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button startIcon={<Chat />} onClick={() => { setDetailOpen(false); openMessageDialog(selectedOrder); }}>
                메시지
              </Button>
              <Button onClick={() => setDetailOpen(false)}>닫기</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 메시지 다이얼로그 */}
      <Dialog open={messageDialog} onClose={closeMessageDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontWeight={600}>메시지 {messageOrder && `- ${messageOrder.order_number}`}</Typography>
          <IconButton onClick={closeMessageDialog}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ height: 400, overflowY: 'auto', p: 2, bgcolor: '#f5f5f5' }}>
            {messages.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ mt: 10 }}>
                메시지가 없습니다. 문의사항을 보내주세요.
              </Typography>
            ) : (
              messages.map((msg) => (
                <Box key={msg.message_id} sx={{
                  display: 'flex', justifyContent: msg.sender_type === 'customer' ? 'flex-end' : 'flex-start', mb: 1
                }}>
                  <Box sx={{
                    maxWidth: '75%', p: 1.5, borderRadius: 2,
                    bgcolor: msg.sender_type === 'customer' ? '#1976d2' : '#fff',
                    color: msg.sender_type === 'customer' ? '#fff' : '#333',
                    boxShadow: 1,
                  }}>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.3 }}>
                      {msg.sender_type === 'customer' ? '나' : (companyName || '직원')} · {new Date(msg.created_at).toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.message}</Typography>
                  </Box>
                </Box>
              ))
            )}
            <div ref={messagesEndRef} />
          </Box>
        </DialogContent>
        <Box sx={{ display: 'flex', p: 1.5, gap: 1, borderTop: '1px solid #e0e0e0' }}>
          <TextField
            fullWidth size="small" placeholder="메시지를 입력하세요..."
            value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
          />
          <IconButton color="primary" onClick={handleSendMessage} disabled={messageLoading || !newMessage.trim()}>
            <Send />
          </IconButton>
        </Box>
      </Dialog>

      {/* 운전자 위치 추적 */}
      <DriverLocationDialog
        open={trackingOpen}
        onClose={() => { setTrackingOpen(false); setTrackingOrder(null); }}
        order={trackingOrder}
      />
    </CustomerLayout>
  );
}
